// Describes an object of parameters that is required for a repository to
// determine where a request should go. E.g. an api might require a request

import { RepositoryEventTarget } from "@/wc/registry/lib/service/repository/base.js";

// to '/api/user/{user_id}', in that case the required parameter is 'user_id'.
export interface IRepositoryParameters {
    [variableName: string]: string | number | string[];
}

export interface IRepositoryActionOptions {
    renewCacheEntry: boolean;
}

// Contains a bunch of types and interfaces to be used by our repositories.
export enum SupportedPropertyTypes {
    String = "string",
    FileType = "file", // not actually supported, but we can show an upload field placeholder, so keeping it here.
    Integer = "integer",
    Number = "number",
    Object = "object",
    Boolean = "boolean",
    // When this type exists, it needs to be processed further before it can be used in form-inputs. It typically
    // also describes the type that needs to be used in the array. If not processed further, we likely do not support
    // that specific array type yet.
    Array = "array",
    ArrayString = "array-string",
    AutocompleteSearch = "autocomplete-search",
}

export interface IFields {
    [name: string]: string | number | IFields | IFields[];
}

export interface IChoiceOptionsExtension {
    "href": string;
    "method": string;
    "key"?: string;
    "key-description"?: string;
}

export interface IStekzPropertyExtension {
    xChoiceOptions?: IChoiceOptionsExtension;
}

export interface IFieldWithSchema {
    properties?: IStekzSchema;
}

export interface IBaseStekzFieldProperties {
    // When type does not exist in SupportedPropertyTypes, we still add it, so
    // when using it in e.g. a form-generator, we can fall back to 'string' when
    // an unsupported type is detected while being explicit about it.
    type: SupportedPropertyTypes | string;
    children?: IStekzSchema[];
    title?: string;
    required?: boolean;
    enum?: string[];
    default?: string;
    description?: string;
    examples?: string[];
    format?: string;
    pattern?: string;
    readOnly?: boolean;
    dependsOn?: string;
    extensions?: IStekzPropertyExtension;
    autocomplete?: string;
}

export type IStekzFieldProperties = IBaseStekzFieldProperties &
    IFieldWithSchema;
export type IInputProps = Omit<IStekzFieldProperties, "type">;

export interface IDiscriminator {
    propertyName: string;
}

export interface IPolymorphicStekzSchemaBase {
    oneOf?: IStekzSchema[];
    allOf?: IStekzSchema[];
    anyOf?: IStekzSchema[];
    discriminator?: IDiscriminator;
}

// The default expected schema differs per repository action. When a schema
// is flattened for an 'all' repository action, the type information will
// be omitted from the specific repository.properties.all call, since it will
// be expected to be an array as implied by the 'all' to make property lookup
// easier for repository consumers. However in some cases the type information
// differs a bit from what you would expect it to be for a given action. For
// example, the 'update' action typically expects an object, so no 'object'
// type will be added to the root of the schema. However when an 'update' action
// requires an array, the 'array' type will be added to the root of the schema,
// and the actual fields required per object in the array, will be nested in
// the 'properties' field. This shouldn't happen often, hence the non default
// schema name.
export interface IStekzNonDefaultSchemaBase {
    type?: SupportedPropertyTypes;
    properties?: IStekzSchema;
}

export interface IStekzSchemaBase {
    [name: string]: IStekzFieldProperties;
}

export type IStekzSchema = IStekzSchemaBase &
    IPolymorphicStekzSchemaBase &
    IStekzNonDefaultSchemaBase;

export interface IActionPathAllParameters {
    pathParameters: string[];
    queryParameters: string[];
}

export interface IActionPathConfiguration {
    method?: string;
    path?: string;
    requiredParameters: string[];
    allParameters?: IActionPathAllParameters;
    // Stores either the request or response schema for a given request,
    // depending on the action. E.g. a 'GET' request will store the response
    // schema, while a 'POST' request will store the request schema.
    properties?: IStekzSchema;
    // Stores the response schema for a given request, if it exists.
    response?: IStekzSchema;
    requestBodyContentType?: SupportedContentTypes;
    info?: {
        isPaginated: boolean;
        isSearchable: boolean;
        isSortable: boolean;
        hasItemActions: boolean;
    };
}

export type RepositoryAction =
    | "create"
    | "update"
    | "partialUpdate"
    | "delete"
    | "get"
    | "all";
export type ActionPaths = Partial<{
    [key in RepositoryAction]: IActionPathConfiguration;
}>;

// Still returning a lot of types since we haven't implemented an interface
// yet to deal with errors etc. Should be a part of ticket #18.
export interface IRepositoryManager {
    repository: IRepository;
    get?: (
        props: IRepositoryParameters,
        fields?: IFields | FormData,
        options?: IRepositoryActionOptions
    ) => Promise<Response>;
    all?: (
        props: IRepositoryParameters,
        fields?: IFields | FormData,
        options?: IRepositoryActionOptions
    ) => Promise<Response>;
    delete?: (
        props: IRepositoryParameters,
        fields?: IFields | FormData,
        options?: IRepositoryActionOptions
    ) => Promise<Response>;
    update?: (
        props: IRepositoryParameters,
        fieldsToUpdate: IFields | FormData,
        options?: IRepositoryActionOptions
    ) => Promise<Response>;
    partialUpdate?: (
        props: IRepositoryParameters,
        fieldsToUpdate: IFields | FormData,
        options?: IRepositoryActionOptions
    ) => Promise<Response>;
    create?: (
        props: IRepositoryParameters,
        fieldsToCreate: IFields | FormData,
        options?: IRepositoryActionOptions
    ) => Promise<Response>;
    test: (action: string, props: IRepositoryParameters) => boolean;
}

export interface IRepositoryPropertiesChecker {
    repository: IRepository;
    get: IStekzSchema;
    all: IStekzSchema;
    delete: IStekzSchema;
    update: IStekzSchema;
    partialUpdate: IStekzSchema;
    create: IStekzSchema;
}

export interface IRepository {
    name: string;
    events: RepositoryEventTarget;
    objects: IRepositoryManager;
    actionPaths: ActionPaths;
    properties: IRepositoryPropertiesChecker;

    getActionForMethod?(method: string): RepositoryAction | undefined;
}

export interface IRepositoryContainer {
    [repositoryName: string]: IRepository;
}

export enum SupportedConfigTypes {
    Custom = "custom",
    OpenApi = "openapi",
}

export type SupportedContentTypes =
    | "application/json"
    | "multipart/form-data"
    | "application/x-www-form-urlencoded";

export interface IModificationsProperties {
    get?: IStekzSchema | Error;
    all?: IStekzSchema | Error;
    delete?: IStekzSchema | Error;
    update?: IStekzSchema | Error;
    partialUpdate?: IStekzSchema | Error;
    create?: IStekzSchema | Error;
}

export interface IModificationsObjects {
    get?: (props: IRepositoryParameters) => Promise<Response>;
    create?: (
        props: IRepositoryParameters,
        fields?: IFields | FormData
    ) => Promise<Response>;
    update?: (
        props: IRepositoryParameters,
        fields?: IFields | FormData
    ) => Promise<Response>;
    partialUpdate?: (
        props: IRepositoryParameters,
        fields?: IFields | FormData
    ) => Promise<Response>;
    all?: (props: IRepositoryParameters) => Promise<Response>;
    delete?: (props: IRepositoryParameters) => Promise<Response>;
}

export interface IModifications {
    objects?: IModificationsObjects;
    properties?: IModificationsProperties;
    ordering?: {
        get?: string[] | Error;
        all?: string[] | Error;
        delete?: string[] | Error;
        update?: string[] | Error;
        partialUpdate?: string[] | Error;
        create?: string[] | Error;
    };
}

export interface IConfig {
    type: SupportedConfigTypes;
    // Only defined for the OpenApi type, since paths are used to determine how
    // a repository be parsed.
    paths?: string[];
    // Can be used to add or adapt functions of a repository.
    modifications?: IModifications;
    // not sure if worth mentioning, but this only exists when the type is
    // openapi ;).
    openApiFileLocation?: string;
    // Whether or not to avoid automatically linking fields that are required
    // for an action to related fields from other repositories.
    avoidLinking?: boolean;
    customRepositoryClass?: new () => IRepository;
}

export interface ICacheConfig {
    // The actions that can be cached.
    cachedActions: {
        [key in RepositoryAction]?: {
            // When the cached action will be invalidated - requests after
            // invalidation will refresh the cache.
            "invalidate-on": RepositoryAction[];
        };
    };
}

export interface IDynamicItemAction {
    href: string;
    method: string;
    name: string;
    title?: string;
    operation?: string;
}
